import React, {useEffect, useState} from "react";
import {Alert, Box, Divider, Button} from "@mui/material";
import {CardElement, useElements, useStripe} from "@stripe/react-stripe-js";
import {subscriptionServices} from "../../service/subscriptionService";
import Spinner from "../spinner/Spinner";
import {DialogActions, DialogContent} from "@mui/material";
import {AuthCard, AuthCardField, AuthLayout, AuthPaymentNote} from "./AuthDesign";
import {getSettingsColors} from "../settings/SettingsDesign";
import CustomSnackbar from "../alert/CustomSnackbar";
import {connect} from "react-redux";

function RetryInvoice(props) {

    const {auth} = props;

    const stripe = useStripe();
    const elements = useElements();

    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [subscriptionInfo, setSubscriptionInfo] = useState("");
    const [subscriptionStatus, setSubscriptionStatus] = useState("past due");

    const handleRetryInvoice = async event => {
        event.preventDefault();

        setLoading(true);
        setErrorMsg("");

        if (!stripe || !elements) {
            return;
        }

        const {error, paymentMethod} = await stripe.createPaymentMethod({
            type: "card",
            card: elements.getElement(CardElement)
        });

        if (error) {
            setLoading(false);
            setErrorMsg(error?.message);
            return;
        }

        await subscriptionServices.retryInvoice(paymentMethod?.id)
            .then(async (result) => {
                if (result?.status === "active") {
                    document.location.replace("/");
                } else if (result?.paymentIntentStatus === "requires_action" || result?.paymentIntentStatus === "requires_payment_method") {
                    await stripe.confirmCardPayment(result?.paymentIntentClientSecret, {payment_method: result?.paymentMethodId})
                        .then((result) => {
                            if (result?.error) {
                                setErrorMsg(result?.error?.message);
                            } else {
                                if (result.paymentIntent.status === 'succeeded') {
                                    // There's a risk of the customer closing the window before callback
                                    // execution. To handle this case, set up a webhook endpoint and
                                    // listen to invoice.paid. This webhook endpoint returns an Invoice.
                                    subscriptionServices.updateSubscriptionStatus().then(() => {
                                        document.location.replace("/");
                                    }).catch((error) => {
                                        setErrorMsg(error?.response?.data?.error);
                                    });
                                }
                            }
                        });
                }
            }).catch((error) => {
                setErrorMsg(error?.response?.data?.error);
            }).finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        subscriptionServices.fetchSubscription(false, false, true, true).then((result) => {
            setSubscriptionInfo(result);
        }).catch((error) => {
            setErrorMsg(error?.response?.data?.error);
        }).finally(() => {
            setLoading(false);
        });
    }, []);

    useEffect(() => {
        if (auth?.subscription?.status && auth?.subscription?.status !== "") {
            if (auth?.subscription?.status === "past_due") {
                setSubscriptionStatus("past due");
            } else {
                setSubscriptionStatus(auth?.subscription?.status);
            }
        }
    }, [auth?.subscription?.status]);

    return <AuthLayout session><AuthCard title="Update your payment method" description="Review your outstanding balance and add a card to renew your subscription.">
        {loading && <Spinner size={32} overComponentBox/>}
        <DialogContent>
            <Alert severity="warning" variant="filled">Your subscription status is {subscriptionStatus}. Please update your payment method.</Alert>
            <Box sx={{bgcolor: theme => getSettingsColors(theme).inset, borderRadius: '10px', p: 2, my: 3}}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', gap: 2, mb: 1.5, fontSize: 14}}>
                    <Box sx={{color: 'text.secondary'}}>Subscription plan</Box><Box sx={{fontWeight: 600}}>{subscriptionInfo?.plan?.name || '—'}</Box>
                </Box>
                <Divider/>
                <Box sx={{display: 'flex', justifyContent: 'space-between', gap: 2, mt: 1.5, fontSize: 16, fontWeight: 600}}>
                    <Box>Total due now</Box><Box>{subscriptionInfo?.amountRemaining != null ? `€ ${(subscriptionInfo.amountRemaining / 100).toFixed(2)}` : '—'}</Box>
                </Box>
            </Box>
            {errorMsg !== "" && <CustomSnackbar message={errorMsg} severity="error" timeout={3500}/>}
            <form id="retry-invoice" onSubmit={handleRetryInvoice}>
                <Box sx={{fontWeight: 600, fontSize: 14}}>Card details</Box>
                <AuthCardField/>
            </form>
        </DialogContent>
        <DialogActions><Button type="submit" form="retry-invoice" variant="contained" fullWidth disabled={loading || !stripe || !elements}>
            Confirm payment
        </Button></DialogActions>
        <AuthPaymentNote/>
    </AuthCard></AuthLayout>;
}

const mapStateToProps = (state) => {
    return {
        auth: state.auth
    };
};

export default connect(mapStateToProps, null)(RetryInvoice);
